import Rails from "@rails/ujs";
Rails.start();

if (!String.prototype.endsWith) {
  String.prototype.endsWith = function (search, this_len) {
    if (this_len === undefined || this_len > this.length) {
      this_len = this.length;
    }
    return this.substring(this_len - search.length, this_len) === search;
  };
}

/*
Tracument Components

Tracument's web components deal with displaying information to the user.
React components are used for application logic and state management.
Web components encapsulate styling and display in a nice, reusable package.
The goal with our web components is to have a set of base components
that we control.

Alerts

  These show info to the user.

  Components:
  tracument-alert-info
  tracument-alert-success
  tracument-alert-warning
  tracument-alert-danger

  Usage: <tracument-alert-success>Yay! It worked</tracument-alert-success>


Loading Indicators

  This shows that something is loading

  Components:
  tracument-loading-indicator

  Usage: <tracument-loading-indicator></tracument-loading-indicator>


Modals
  This shows modal/dialog information to the user

  Components:
  tracument-modal
  tracument-modal-header
  tracument-modal-body
  tracument-modal-footer

  Usage:
  <tracument-modal open="true">
    <tracument-modal-header id="header_id">Title<tracument-modal-header>
    <tracument-modal-body>
       Content goes here.
    </tracument-modal-body>
    <tracument-modal-footer>
      Footer Content
    </tracument-modal-footer>
  </tracument-modal>

  Events:
  close - document.getElementById(`header_id`).addEventListener("close", () => {});

Popovers
  This shows a popover to the user

  Components:
  tracument-popover
  tracument-popover-header
  tracument-popover-body

  Usage:
  <tracument-popover
    show="true"
    placement="right"  // right | left | top | bottom
    target="target_id"
  >
    <tracument-popover-header>Title</tracument-popover-header>
    <tracument-popover-body>
       Content
    </tracument-popover-body>
  </tracument-popover>

Container
  This contains content

  Components:
  tracument-container

  Usage: <tracument-container>Your content</tracument-container>

Banner
  This is a full width banner.

  Components:
  tracument-top-banner

  Usage: <tracument-top-banner>Content</tracument-top-banner>

Workspace
  These components are for displaying workspaces

  Components:
  tracument-workspace-status-line
  tracument-workspace-header-date
  tracument-workspace-header
  tracument-workspace-section
  tracument-workspace-actions-section
  tracument-workspace-actions-icon
  tracument-workspace-actions

	Usage:
	<tracument-workspace-status-line>Status</tracument-workspace-status-line>

	<tracument-workspace-header-date date="2023-01-01"></tracument-workspace-header-date>

	<tracument-workspace-header>Header Content</tracument-workspace-header>

	<tracument-workspace-section title="Title">
    Content
	</tracument-workspace-section>

	<tracument-workspace-actions-section>
		<tracument-workspace-actions-icon>
				<a className="glyphicons no-js icon_name"><i></i></a> <span>Icon Label</span>
		</tracument-workspace-actions-icon>

		<h2>Actions</h2>
		<tracument-workspace-actions>
      links and/or buttons
		</tracument-workspace-actions>
  </tracument-workspace-actions-section>
*/

const alertCommonStyles = `
  position: var(--alert-position);
  padding: var(--alert-padding);
  margin-bottom: var(--alert-margin-bottom);
  border: var(--alert-border);
  border-radius: var(--alert-border-radius);
  font-family: var(--font-family-sans-serif);
  display: none;
`;

const alertInfoTemplate = document.createElement("template");
alertInfoTemplate.innerHTML = `
  <style>
	  div {
			${alertCommonStyles}
			color: #004085;
      background-color: #cce5ff;
      border-color: #b8daff;
		}
  </style>
  <div>
    <slot></slot>
  </div>
`;

customElements.define(
  "tracument-alert-info",
  class TracumentAlertInfo extends HTMLElement {
    constructor() {
      super();

      let templateContent = alertInfoTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }

    connectedCallback() {
      const slot = this.shadowRoot.querySelector("slot");

      slot.addEventListener("slotchange", (event) => {
        const div = this.shadowRoot.querySelector("div");
        const nodes = event.target.assignedNodes();

        if (nodes.length > 0) {
          div.style.display = "block";
        } else {
          div.style.display = "none";
        }
      });
    }
  }
);

const alertSuccessTemplate = document.createElement("template");
alertSuccessTemplate.innerHTML = `
  <style>
	  div {
			${alertCommonStyles}
			color: #155724;
      background-color: #d4edda;
      border-color: #c3e6cb;
		}
  </style>
  <div>
    <slot></slot>
  </div>
`;

customElements.define(
  "tracument-alert-success",
  class TracumentAlertSuccess extends HTMLElement {
    constructor() {
      super();

      let templateContent = alertSuccessTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }

    connectedCallback() {
      const slot = this.shadowRoot.querySelector("slot");

      slot.addEventListener("slotchange", (event) => {
        const div = this.shadowRoot.querySelector("div");
        const nodes = event.target.assignedNodes();

        if (nodes.length > 0) {
          div.style.display = "block";
        } else {
          div.style.display = "none";
        }
      });
    }
  }
);

const alertWarningTemplate = document.createElement("template");
alertWarningTemplate.innerHTML = `
  <style>
	  div {
			${alertCommonStyles}
			color: #856404;
      background-color: #fff3cd;
      border-color: #ffeeba;
		}
  </style>
  <div>
    <slot></slot>
  </div>
`;

customElements.define(
  "tracument-alert-warning",
  class TracumentAlertWarning extends HTMLElement {
    constructor() {
      super();

      let templateContent = alertWarningTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }

    connectedCallback() {
      const slot = this.shadowRoot.querySelector("slot");

      slot.addEventListener("slotchange", (event) => {
        const div = this.shadowRoot.querySelector("div");
        const nodes = event.target.assignedNodes();

        if (nodes.length > 0) {
          div.style.display = "block";
        } else {
          div.style.display = "none";
        }
      });
    }
  }
);

const alertDangerTemplate = document.createElement("template");
alertDangerTemplate.innerHTML = `
  <style>
	  div {
			${alertCommonStyles}
			color: #721c24;
			background-color: #f8d7da;
			border-color: #f5c6cb;
		}
  </style>
  <div>
    <slot></slot>
  </div>
`;

customElements.define(
  "tracument-alert-danger",
  class TracumentAlertDanger extends HTMLElement {
    constructor() {
      super();

      let templateContent = alertDangerTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }

    connectedCallback() {
      const slot = this.shadowRoot.querySelector("slot");

      slot.addEventListener("slotchange", (event) => {
        const div = this.shadowRoot.querySelector("div");
        const nodes = event.target.assignedNodes();

        if (nodes.length > 0) {
          div.style.display = "block";
        } else {
          div.style.display = "none";
        }
      });
    }
  }
);

const loadingIndicatorTemplate = document.createElement("template");
loadingIndicatorTemplate.innerHTML = `
  <style>
	  div {
			margin-top: 20px;
			clear: both;
			border: 16px solid #f3f3f3; /* Light grey */
			border-top: 16px solid #3498db; /* Blue */
			border-radius: 50%;
			width: 120px;
			height: 120px;
			animation: spin 2s linear infinite;
			margin-right: auto;
			margin-left: auto;
    }

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
  </style>
  <div>
  </div>
`;

customElements.define(
  "tracument-loading-indicator",
  class TracumentLoadingIndicator extends HTMLElement {
    constructor() {
      super();

      let templateContent = loadingIndicatorTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }
  }
);

const modalTemplate = document.createElement("template");
modalTemplate.innerHTML = `
  <style>
	  dialog {
      background-color: #fff;
		  border: 1px solid rgba(0,0,0,.2);
		  border-radius: var(--border-radius);
		  outline: 0;
			width: 800px;
			padding: 0px;
	  }
		dialog::backdrop {
			background-color: rgba(0,0,0,0.4);
		}
  </style>

	<dialog>
    <slot></slot>
  </dialog>
`;

customElements.define(
  "tracument-modal",
  class TracumentModal extends HTMLElement {
    static observedAttributes = ["open", "size"];

    constructor() {
      super();

      let templateContent = modalTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (name === "open") {
        if (oldVal !== newVal) {
          const dialog = this.shadowRoot.querySelector("dialog");
          if (newVal === "true") {
            dialog.showModal();
          } else {
            dialog.close();
          }
        }
      }

      if (name === "size") {
        if (oldVal !== newVal) {
          const dialog = this.shadowRoot.querySelector("dialog");
          if (newVal === "xl") {
            dialog.style.width = "1140px";
          } else {
            dialog.style.width = "800px";
          }
        }
      }
    }
  }
);

const modalHeaderTemplate = document.createElement("template");
modalHeaderTemplate.innerHTML = `
  <style>
	  h5 {
      margin-bottom: 0px;
			margin-top: 0px;
      line-height: 1.5;
			font-family: var(--heading-font-family);
			color: var(--heading-color);
			font-size: 1.25rem;
			font-weight: normal;
		}

	  div {
			display: flex;
			align-items: flex-start;
			justify-content: space-between;
			padding: 1rem 1rem;
			border-bottom: var(--border);
			border-top-left-radius: var(--border-radius);
			border-top-right-radius: var(--border-radius);
		}

		button {
			padding: 1rem 1rem;
			margin: -1rem -1rem -1rem auto;
			background-color: transparent;
			border: 0;
			appearance: none;
			float: right;
			font-size: 1.5rem;
			font-weight: 700;
			line-height: 1;
			color: #000;
			text-shadow: 0 1px 0 #fff;
			opacity: .5;
			cursor: pointer;
		}
  </style>

	<div>
      <h5>
        <slot></slot>
      </h5>
      <button
        type="button"
        aria-label="Close"
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
`;

customElements.define(
  "tracument-modal-header",
  class TracumentModalHeader extends HTMLElement {
    constructor() {
      super();

      let templateContent = modalHeaderTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }

    connectedCallback() {
      const button = this.shadowRoot.querySelector("button");

      button.addEventListener("click", () => {
        this.dispatchEvent(
          new CustomEvent("close", {
            detail: "Close Modal",
          })
        );
      });
    }
  }
);

const modalBodyTemplate = document.createElement("template");
modalBodyTemplate.innerHTML = `
  <style>
	  div {
			position: relative;
      flex: 1 1 auto;
      padding: 1rem;
			font-family: var(--font-family-sans-serif);
			color: var(--font-color);
		}
  </style>
  <div>
      <slot></slot>
  </div>
`;

customElements.define(
  "tracument-modal-body",
  class TracumentModalBody extends HTMLElement {
    constructor() {
      super();

      let templateContent = modalBodyTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }
  }
);

const modalFooterTemplate = document.createElement("template");
modalFooterTemplate.innerHTML = `
  <style>
	  div {
			display: flex;
			align-items: center;
			justify-content: flex-end;
			padding: 1rem;
			border-top: var(--border);
			border-bottom-right-radius: var(--border-radius);
			border-bottom-left-radius: var(--border-radius);
			font-family: var(--font-family-sans-serif);
			clear: both;
		}

		::slotted(*) {
			margin-left: 0.25rem !important;
		}
  </style>
  <div>
      <slot></slot>
  </div>
`;

customElements.define(
  "tracument-modal-footer",
  class TracumentModalFooter extends HTMLElement {
    constructor() {
      super();

      let templateContent = modalFooterTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }
  }
);

const workspaceStatusLineTemplate = document.createElement("template");
workspaceStatusLineTemplate.innerHTML = `
  <style>
	  div {
			color: red;
      font-family: monospace;
      text-transform: uppercase;
      font-weight: bold;
      font-size: 30px;
      margin-top: -10px;
      min-height: 30px;
    }
  </style>
  <div>
    <slot></slot>
  </div>
`;

customElements.define(
  "tracument-workspace-status-line",
  class TracumentWorkspaceStatusLine extends HTMLElement {
    constructor() {
      super();

      let templateContent = workspaceStatusLineTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }
  }
);

const workspaceHeaderDateTemplate = document.createElement("template");
workspaceHeaderDateTemplate.innerHTML = `
  <style>
	  div {
			float: right;
			text-decoration: underline;
			clear: both;
			padding-right: 20px;
      margin-top: -5px;
			font-family: var(--font-family-sans-serif);
    }
  </style>
  <div>
  </div>
`;

customElements.define(
  "tracument-workspace-header-date",
  class TracumentWorkspaceHeaderDate extends HTMLElement {
    static observedAttributes = ["date"];
    constructor() {
      super();

      let templateContent = workspaceHeaderDateTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (name === "date") {
        if (oldVal !== newVal) {
          const div = this.shadowRoot.querySelector("div");
          div.innerHTML = new Date(newVal).toLocaleDateString("en-ca", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
          });
        }
      }
    }
  }
);

customElements.define(
  "tracument-workspace-header-basic-date",
  class TracumentWorkspaceHeaderBasicDate extends HTMLElement {
    static observedAttributes = ["date"];
    constructor() {
      super();

      let templateContent = workspaceHeaderDateTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (name === "date") {
        if (oldVal !== newVal) {
          const div = this.shadowRoot.querySelector("div");
          const newValWithTime = newVal + "T00:00:00";
          div.innerHTML = new Date(newValWithTime).toLocaleDateString("en-ca", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        }
      }
    }
  }
);

const workspaceHeaderTemplate = document.createElement("template");
workspaceHeaderTemplate.innerHTML = `
  <style>
	  section {
			display: block;
			width: 920px;
      float: left;
			font-family: var(--font-family-sans-serif);
		}
  </style>
  <section>
    <slot></slot>
  </section>
`;

customElements.define(
  "tracument-workspace-header",
  class TracumentWorkspaceHeader extends HTMLElement {
    constructor() {
      super();

      let templateContent = workspaceHeaderTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }
  }
);

const workspaceSectionTemplate = document.createElement("template");
workspaceSectionTemplate.innerHTML = `
  <style>
	  section {
			clear: both;
			border: 1px solid #ddd;
      border-radius: 4px;
      margin-bottom: 20px;
			font-family: var(--font-family-sans-serif);
		}

    h2 {
      background-color: #ddd;
      margin-top: 0px;
      padding-left: 10px;
			font-size: 1.6rem;
			font-weight: normal;
			color: var(--heading-color);
			font-family: var(--heading-font-family);
    }
  </style>
  <section>
	  <h2></h2>
    <slot></slot>
  </section>
`;

customElements.define(
  "tracument-workspace-section",
  class TracumentWorkspaceSection extends HTMLElement {
    static observedAttributes = ["title"];

    constructor() {
      super();

      let templateContent = workspaceSectionTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (name === "title") {
        if (oldVal !== newVal) {
          const h2 = this.shadowRoot.querySelector("h2");
          h2.innerHTML = newVal;
        }
      }
    }
  }
);

const workspaceActionsSectionTemplate = document.createElement("template");
workspaceActionsSectionTemplate.innerHTML = `
  <style>
	  section {
			border-left: 1px solid #EEEEEE;
			padding-left: 15px;
			margin-top: -40px;
			width: 200px;
			float: left;
			display: block;
		}
  </style>
  <section>
    <slot></slot>
  </section>
`;

customElements.define(
  "tracument-workspace-actions-section",
  class TracumentWorkspaceActionsSection extends HTMLElement {
    constructor() {
      super();

      let templateContent = workspaceActionsSectionTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }
  }
);

const workspaceActionsIconTemplate = document.createElement("template");
workspaceActionsIconTemplate.innerHTML = `
  <style>
	  div {
			margin-bottom: 10px;
		}
		div:after {
      content: "";
      clear: both;
      display: table;
		}
	  ::slotted(a) {
			float: left;
		}
		::slotted(span) {
			float: left;
			margin-top: 5px;
		}
  </style>
  <div>
    <slot></slot>
  </div>
`;

customElements.define(
  "tracument-workspace-actions-icon",
  class TracumentWorkspaceActionsIcon extends HTMLElement {
    constructor() {
      super();

      let templateContent = workspaceActionsIconTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }
  }
);

const workspaceActionsTemplate = document.createElement("template");
workspaceActionsTemplate.innerHTML = `
  <style>
    ::slotted(a), ::slotted(button) {
      width: 190px;
			display: block;
			margin-bottom: 10px !important;
		}
  </style>
  <div>
    <slot></slot>
  </div>
`;

customElements.define(
  "tracument-workspace-actions",
  class TracumentWorkspaceActions extends HTMLElement {
    constructor() {
      super();

      let templateContent = workspaceActionsTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }
  }
);

const containerTemplate = document.createElement("template");
containerTemplate.innerHTML = `
  <style>
	  div {
		  width: 100%;
		  padding-right: 15px;
		  padding-left: 15px;
		  margin-right: auto;
		  margin-left: auto;
    }
		@media (min-width: 1200px) {
			div {
				min-width: 1170px;
				max-width: 1170px;
			}
		}
		@media (min-width: 992px) {
			div {
				max-width: 960px;
				padding-left: 0px;
			  padding-right: 0px;
			}
		}
		@media (min-width: 768px) {
			div {
				max-width: 720px;
			}
		}
		@media (min-width: 576px) {
		  div {
				max-width: 540px;
		  }
		}
  </style>
  <div>
    <slot></slot>
  </div>
`;

customElements.define(
  "tracument-container",
  class TracumentContainer extends HTMLElement {
    constructor() {
      super();

      let templateContent = containerTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }
  }
);

const topBannerTemplate = document.createElement("template");
topBannerTemplate.innerHTML = `
  <style>
	  div {
			margin-top: -10px;
			padding-top: 15px;
			padding-bottom: 1px;
			text-align: center;
			background-color: var(--tracument-red);
			color: #FFFFFF;
		}

		p, a {
			color: #FFFFFF;
		}

		a:hover {
			color: #FFFFFF;
			text-decoration: underline;
		}
  </style>
  <div>
    <slot></slot>
  </div>
`;

customElements.define(
  "tracument-top-banner",
  class TracumentTopBanner extends HTMLElement {
    constructor() {
      super();

      let templateContent = topBannerTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }
  }
);

const popoverTemplate = document.createElement("template");
popoverTemplate.innerHTML = `
  <style>
    [popover] {
      position: absolute;
			will-change: transform;
			top: 0px;
			left: 0px;
      transform: translate3d(0px, 0px, 0px);
			max-width: 276px !important;
      width: 276px !important;
			font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans","Liberation Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
			font-style: normal;
			font-weight: 400;
			line-height: 1.5;
			text-align: left;
			text-decoration: none;
			text-shadow: none;
			text-transform: none;
			letter-spacing: normal;
			word-break: normal;
			word-spacing: normal;
			white-space: normal;
			line-break: auto;
			font-size: .875rem;
			word-wrap: break-word;
			background-color: #fff;
			background-clip: padding-box;
			border: 1px solid rgba(0,0,0,.2);
			border-radius: 0.3rem;
      padding: 0px;
      margin: 0px;
    }
  </style>
  <div popover>
    <slot></slot>
  </div>
`;

customElements.define(
  "tracument-popover",
  class TracumentPopover extends HTMLElement {
    static observedAttributes = ["placement", "show", "target"];
    constructor() {
      super();

      let templateContent = popoverTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (oldVal !== newVal) {
        const div = this.shadowRoot.querySelector("div");
        if (name === "show") {
          if (newVal === "true") {
            div.showPopover();
          } else {
            div.hidePopover();
          }
          this.updateLocation();
        }
      }
    }

    updateLocation() {
      const popover = this.shadowRoot.querySelector("[popover]");

      const target = this.getAttribute("target");
      const placement = this.getAttribute("placement");

      if (target && placement) {
        const rect = document.getElementById(target).getBoundingClientRect();
        const popoverRect = popover.getBoundingClientRect();

        if (placement === "right") {
          popover.style.left = `${rect.right + 20}px`;
          popover.style.top = `${
            rect.top + rect.height / 2 + window.scrollY - popoverRect.height / 2
          }px`;
        } else if (placement === "top") {
          popover.style.left = `${
            rect.left + rect.width / 2 - rect.width / 2
          }px`;
          popover.style.top = `${
            rect.top + window.scrollY - popoverRect.height - 20
          }px`;
        } else if (placement === "bottom") {
          popover.style.left = `${
            rect.left + rect.width / 2 - rect.width / 2
          }px`;
          popover.style.top = `${
            rect.top + window.scrollY + rect.height + 20
          }px`;
        } else {
          //left
          popover.style.left = `${rect.left - 20 - popoverRect.width}px`;
          popover.style.top = `${
            rect.top + rect.height / 2 + window.scrollY - popoverRect.height / 2
          }px`;
        }
      }
    }
  }
);

const popoverHeaderTemplate = document.createElement("template");
popoverHeaderTemplate.innerHTML = `
  <style>
	  h3 {
			padding: 0.5rem 0.75rem;
			margin: 0;
			font-size: 1rem;
			background-color: #f7f7f7;
			border-bottom: 1px solid #ebebeb;
			border-top-left-radius: calc(0.3rem - 1px);
			border-top-right-radius: calc(0.3rem - 1px);
			font-family: var(--heading-font-family);
	    color: var(--heading-color);
			font-weight: normal;
		}
  </style>
  <h3>
    <slot></slot>
  </h3>
`;

customElements.define(
  "tracument-popover-header",
  class TracumentPopoverheader extends HTMLElement {
    constructor() {
      super();

      let templateContent = popoverHeaderTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }
  }
);

const popoverBodyTemplate = document.createElement("template");
popoverBodyTemplate.innerHTML = `
  <style>
	  div {
			padding: 0.5rem 0.75rem;
			color: #212529;
		}
  </style>
  <div>
    <slot></slot>
  </div>
`;

customElements.define(
  "tracument-popover-body",
  class TracumentPopoverBody extends HTMLElement {
    constructor() {
      super();

      let templateContent = popoverBodyTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }
  }
);

const adButtonTemplate = document.createElement("template");
adButtonTemplate.innerHTML = `
  <style>
    * {
	  box-sizing: border-box;
	}
    tracument-ad-button {
      vertical-align: middle;
    }
    a.btn {
      display: inline-block;
      width: auto;
      height: auto;
      /* min-height: var(--button_height_md); */
      padding-block: var(--button_padding_block);
      padding-inline: var(--button_padding_inline);
      font-family: var(--font_stack_buttons);
      font-size: var(--font_size_body);
      font-weight: var(--font_weight_buttons);
      line-height: var(--button_line-height);
      text-decoration: none;
      color: var(--colour_white);
      border-width: var(--button_border_width);
      border-style: solid;
      border-color: var(--colour_red_medium);
      background: var(--colour_red_medium);
      border-radius: var(--button_border_radius);
      transition: 
        var(--transition_colour), 
        var(--transition_background), 
        var(--transition_border), 
        var(--transition_box-shadow), 
        var(--transition_text-shadow), 
        var(--transition_transform) 
        !important;
    }
    .btn:hover, .btn:focus-visible, .btn:active {
      color: var(--colour_red_medium);
      background-color: var(--colour_white);
      border-color: var(--colour_red_medium);
    }
    .btn.focus, .btn:focus {
      box-shadow: 0 0 0 0.2rem rgba(var(--colour_grey_light_rgb), 0.5);
    }
    .btn.activated {
      color: var(--colour_red_medium);
      background-color: var(--colour_white);
      border-color: var(--colour_red_medium);
    }
    .growOnHover:hover {
      transform: var(--button_hover_transform);
    }
  </style>
  <a class="appeal__button btn"><slot></slot></a>
`;

customElements.define(
  "tracument-ad-button",
  class TracumentAdButton extends HTMLElement {
    // Constructor
    constructor() {
      super();

      let templateContent = adButtonTemplate.content;

      const shadowRoot = this.attachShadow({ mode: "open" }).appendChild(
        templateContent.cloneNode(true)
      );
    }

    // Connected callback
    connectedCallback() {
      this.link = this.shadowRoot.querySelector("a");
      if (!this.link) return;

      // Iterate through the custom element's attributes
      Array.from(this.attributes).forEach((attr) => {
        // Additional classes to add?
        if (attr.nodeName == "additional-classes") {
          if (attr.nodeValue == "") return;

          // this.link.classList.add(attr.nodeValue); // Will fail if there's a space, i.e. multiple class names, so...

          // Are there multiple class names?
          attr.nodeValue.split(" ").forEach((additionalClass) => {
            this.link.classList.add(additionalClass);
          });

          // Otherwise, add the attr.nodeValue as-is
        } else {
          this.link.setAttribute(attr.nodeName, attr.nodeValue);
        }
      });

      // Add event listeners
      this.link.addEventListener("click", this);
    }

    // Handle events
    handleEvent(event) {
      this[`on${event.type}`](event);
    }

    // Click events
    onclick(event) {
      event.preventDefault();
      alert(
        "The Book a Demo button was clicked!\n\nCancel navigating to " +
          this.getAttribute("href")
      );
    }
  }
);
